/**
 * /well-servicing/rigs — the tenant's service rigs
 * (GET /well-servicing/rigs, fieldiq-api#1269, WS-1).
 *
 * Full-width register; a row opens the rig on its own page, where its crew
 * templates live. A rig is a spec on a unit in Equipment, so the unit's name
 * and number sit under the rig number.
 *
 * The registry column is the CAOEC number in Canada and the contractor's own
 * number in the US (meta.country). The mast rating shows as entered, with the
 * API's kN copy beside it. Absent is `—`.
 *
 * New Rig is WS_ADMIN (admin, office_staff) — lib/well-servicing.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck, Users, UserX } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWsMeta, useWsRigs } from '../../hooks/useWellServicing';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminWs, catalogLabel, mastRatingText, rigRegistryLabel } from '../../lib/well-servicing';
import type { WsRig } from '../../types/well-servicing';
import { useOpenRecord } from '../downhole/recordPage';
import { WS_EYEBROW, WS_RAIL, WsEmpty, WsPanel, WsRegister, type WsColumn } from './WsBits';

export default function WsRigsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const catalog = useWsMeta().data?.data ?? null;
  const query = useWsRigs();
  const rigs = query.data?.data ?? [];
  const meta = query.data?.meta ?? null;
  const loading = query.isLoading;
  const known = loading || queryLoadFailure(query) ? undefined : rigs;
  const canAdd = canAdminWs(user?.role);
  const registry = rigRegistryLabel(meta);

  const columns: WsColumn<WsRig>[] = [
    {
      key: 'rig',
      header: 'Rig',
      render: (r) => (
        <>
          <p className="font-extrabold text-white">{r.rigNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {r.equipmentName || '—'}
            {r.unitNumber ? ` · ${r.unitNumber}` : ''}
          </p>
        </>
      ),
    },
    { key: 'registry', header: registry, render: (r) => <span className="font-mono text-sm">{r.caoecRigNumber || '—'}</span> },
    { key: 'class', header: 'Class', render: (r) => r.rigClassLabel ?? catalogLabel(catalog?.rigClasses, r.rigClass) },
    { key: 'mast', header: 'Mast rating', render: (r) => <span className="font-mono text-sm">{mastRatingText(r)}</span> },
    { key: 'crews', header: 'Crews', align: 'right', render: (r) => <span className="font-mono text-sm">{displayValue(r.crewCount)}</span> },
  ];

  return (
    <div data-testid="ws-rigs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="Rigs"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/well-servicing/rigs/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New rig
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Rigs', sub: 'Registered', tone: 'neutral', loading, value: displayValue(known?.length) },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'Crews',
            sub: 'Templates',
            tone: 'neutral',
            loading,
            value: displayValue(known ? known.reduce((n, r) => n + (r.crewCount ?? 0), 0) : undefined),
          },
          {
            icon: <UserX className="size-4" aria-hidden="true" />,
            label: 'No crew',
            tone: 'accent',
            loading,
            value: displayValue(known ? known.filter((r) => !r.crewCount).length : undefined),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WsPanel
            eyebrow="Rig register"
            title="Rigs"
            testId="ws-rig-register"
            footer={!loading && known && rigs.length > 0 ? `${rigs.length} rig${rigs.length === 1 ? '' : 's'}` : undefined}
          >
            <WsRegister
              query={query}
              rows={rigs}
              columns={columns}
              rail={() => WS_RAIL}
              onOpen={(r) => openRecord(`/well-servicing/rigs/${r.id}`)}
              subject="rigs"
              testId="ws-rig"
              minWidth="min-w-[760px]"
              empty={<WsEmpty>There are no rigs yet.{canAdd ? ' Add one from a unit in Equipment.' : ''}</WsEmpty>}
              card={(r) => (
                <>
                  <p className="text-sm font-extrabold text-white">{r.rigNumber || '—'}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    {r.equipmentName || '—'} · {r.rigClassLabel ?? catalogLabel(catalog?.rigClasses, r.rigClass)}
                  </p>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {registry} {r.caoecRigNumber || '—'} · {mastRatingText(r)} · {displayValue(r.crewCount)} crew{r.crewCount === 1 ? '' : 's'}
                  </p>
                </>
              )}
            />
          </WsPanel>
        </div>
      </section>
    </div>
  );
}
